import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/shared/shadcn/ui/dialog';

export interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  onYes?: () => void; // Third button handler
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  yesText?: string; // Third button text
  icon?: React.ReactNode;
  iconType?: 'decline' | 'resend' | 'unlink' | 'delete';
  isLoading?: boolean;
  showCancel?: boolean;
  showConfirm?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  footerClassName?: string;
  confirmButtonClassName?: string;
  cancelButtonClassName?: string;
  yesButtonClassName?: string; // Third button styling
  autoCloseDelay?: number;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  onYes,
  title = 'Confirm Action',
  description = 'Are you sure you want to proceed with this action?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  yesText,
  icon,
  iconType,
  isLoading = false,
  showCancel = true,
  showConfirm = true,
  size = 'md',
  className = '',
  titleClassName = '',
  descriptionClassName = '',
  footerClassName = '',
  confirmButtonClassName = '',
  cancelButtonClassName = '',
  yesButtonClassName = '',
  autoCloseDelay,
}) => {
  // Auto-close functionality
  React.useEffect(() => {
    if (isOpen && autoCloseDelay && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoCloseDelay, onClose]);

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'max-w-sm';
      case 'md':
        return 'max-w-md';
      case 'lg':
        return 'max-w-lg';
      case 'xl':
        return 'max-w-xl';
      default:
        return 'max-w-md';
    }
  };

  const getIconByType = () => {
    if (icon) return icon;
    if (!iconType) return null;

    switch (iconType) {
      case 'decline':
        return <i className="bi bi-link-45deg text-danger text-[42px]"></i>;
      case 'resend':
        return <i className="bi bi-cursor text-slate-text text-[42px]"></i>;
      case 'unlink':
        return <i className="bi bi-exclamation-triangle text-warning text-[42px]"></i>;
      case 'delete':
        return <i className="bi bi-trash text-danger text-[42px]"></i>;
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className={`bg-white box-border content-stretch flex flex-col items-start justify-center p-[24px] rounded-lg-2plus gap-0 [&>button]:hidden w-[92%] sm:w-full ${getSizeClasses()} ${className}`}>
         {/* Icon Section */}
         {(icon || iconType) && (
           <div className="flex items-center justify-center">
             {getIconByType()}
           </div>
         )}
        
        {/* Title Section */}
        <DialogTitle asChild>
          <div className={`font-medium !leading-[1.2] relative shrink-0 text-ink text-2xl [font-variation-settings:'wdth'_100] ${titleClassName}`}>
            {title}
          </div>
        </DialogTitle>

        {/* Description Section */}
        {description && (
          <DialogDescription asChild>
            <p className={`font-normal leading-[1.5] relative shrink-0 text-base pt-2 text-black [font-variation-settings:'wdth'_100] ${descriptionClassName}`}>
              {description}
            </p>
          </DialogDescription>
        )}
        
        {/* Buttons Section */}
        {(showCancel || showConfirm) && (
          <div className={`box-border content-stretch flex flex-col sm:flex-row gap-[16px] items-stretch sm:items-start justify-center pb-0 pt-[24px] px-0 relative shrink-0 w-full ${footerClassName}`}>
            <div className="hidden sm:block basis-0 bg-white grow h-[22px] min-h-px min-w-px shrink-0" />
            
            {showCancel && (
              <button
                className={`bg-white cursor-pointer relative rounded-sm shrink-0 w-full sm:w-[120px] ${cancelButtonClassName}`}
                onClick={handleCancel}
                disabled={isLoading}
              >
                <div className="box-border content-stretch flex gap-2 items-start justify-center overflow-clip px-[13px] py-[7px] relative w-full sm:w-[120px]">
                  <div className="bg-[rgba(255,255,255,0)] relative shrink-0">
                    <div className="bg-clip-padding border-0 border-[transparent] border-solid box-border content-stretch flex flex-col items-start justify-center overflow-clip relative">
                      <div className="font-normal leading-[0] relative shrink-0 text-secondary-text text-base text-nowrap [font-variation-settings:'wdth'_100]">
                        <p className="leading-[1.5] whitespace-pre">{cancelText}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div aria-hidden="true" className="absolute border border-secondary-text border-solid inset-0 pointer-events-none rounded-sm" />
              </button>
            )}
            
            {showConfirm && (
              <button
                className={`bg-hh-primary cursor-pointer relative rounded-sm shrink-0 w-full sm:w-[120px] ${confirmButtonClassName}`}
                onClick={handleConfirm}
                disabled={isLoading}
              >
                <div className="box-border content-stretch flex gap-2 items-start justify-center overflow-clip px-[13px] py-[7px] relative w-full sm:w-[120px]">
                  <div className="bg-[rgba(255,255,255,0)] relative shrink-0">
                    <div className="bg-clip-padding border-0 border-[transparent] border-solid box-border content-stretch flex flex-col items-start justify-center overflow-clip relative">
                      <div className="font-normal leading-[0] relative shrink-0 text-base text-nowrap text-white [font-variation-settings:'wdth'_100]">
                        {isLoading ? (
                          <div className="flex items-center gap-2 leading-[1.5]">
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            <span>Loading...</span>
                          </div>
                        ) : (
                          <p className="leading-[1.5] whitespace-pre">{confirmText}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div aria-hidden="true" className="absolute border border-hh-primary border-solid inset-0 pointer-events-none rounded-sm" />
              </button>
            )}
            
            {yesText && (
              <button
                className={`bg-hh-primary cursor-pointer relative rounded-sm shrink-0 w-full sm:w-[120px] ${yesButtonClassName}`}
                onClick={onYes}
                disabled={isLoading}
              >
                <div className="box-border content-stretch flex gap-2 items-start justify-center overflow-clip px-[13px] py-[7px] relative w-full sm:w-[120px]">
                  <div className="bg-[rgba(255,255,255,0)] relative shrink-0">
                    <div className="bg-clip-padding border-0 border-[transparent] border-solid box-border content-stretch flex flex-col items-start justify-center overflow-clip relative">
                      <div className="font-normal leading-[0] relative shrink-0 text-base text-nowrap text-white [font-variation-settings:'wdth'_100]">
                        <p className="leading-[1.5] whitespace-pre">{yesText}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div aria-hidden="true" className="absolute border border-hh-primary border-solid inset-0 pointer-events-none rounded-sm" />
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationDialog;
